import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer} from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import AsyncStorage from '@react-native-async-storage/async-storage';
import rootReducer from '../modules/reducers';
import rootSaga from '../modules/sagas';
import {secureStorage} from './secureStorage';
import WizzeeLog from './wizzeeLog';

// ─── Debug flags ──────────────────────────────────────────────────────────────
// Set to false to silence Redux/saga logs without touching anything else.
const REDUX_DEBUG = __DEV__ && false;
// ─────────────────────────────────────────────────────────────────────────────

// One-shot flag: read legacy AsyncStorage persist:root once, then never again.
const PERSIST_MIGRATED_KEY = 'PERSIST_MMKV_MIGRATED_V1';

const persistConfig = {
    key: 'root',
    storage: secureStorage,
    whitelist: ['localization', 'theme', 'yourOrders'],
    version: 1,
    migrate: async state => {
        try {
            const alreadyMigrated = await AsyncStorage.getItem(PERSIST_MIGRATED_KEY);
            if (alreadyMigrated) return state;

            const raw = await AsyncStorage.getItem('persist:root');
            await AsyncStorage.setItem(PERSIST_MIGRATED_KEY, 'true');

            if (raw) {
                // redux-persist double-serialises each slice: the root value is JSON,
                // and each whitelisted key's value is itself a JSON string.
                const parsed = JSON.parse(raw);
                return {
                    localization: parsed.localization ? JSON.parse(parsed.localization) : undefined,
                    theme:        parsed.theme        ? JSON.parse(parsed.theme)        : undefined,
                    yourOrders:   parsed.yourOrders   ? JSON.parse(parsed.yourOrders)   : undefined,
                };
            }
        } catch (e) {
            WizzeeLog.warn('[Persist] AsyncStorage → MMKV migration failed', e);
        }
        return state;
    },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// action logger middleware — logs every dispatched action + next state
const actionLogger = storeAPI => next => action => {
  try {
    WizzeeLog.log('[ACTION]', action.type, action);
  } catch (e) { /* ignore logging errors */ }
  const result = next(action);
  try {
    WizzeeLog.log('[STATE ]', action.type, storeAPI.getState());
  } catch (e) { /* ignore logging errors */ }
  return result;
};

// saga effect monitor — logs every saga CALL + rejections/cancellations
// effectTriggered fires for every effect (PUT, SELECT, TAKE, DELAY…); we filter
// to CALL only so the output stays readable while still showing the full call chain.
const sagaMonitor = REDUX_DEBUG
  ? {
      effectTriggered({ effectId, parentEffectId, label, effect }) {
        if (!effect || effect.type !== 'CALL') return;
        const { fn, args } = effect.payload || {};
        const name = (fn && (fn.name || fn.displayName)) || 'anonymous';
        WizzeeLog.debug(`[SAGA ] CALL #${effectId} (parent #${parentEffectId}) → ${name}`, args);
      },
      effectResolved() {},
      effectRejected(effectId, error) {
        WizzeeLog.warn(`[SAGA ] REJECTED #${effectId}`, error);
      },
      effectCancelled(effectId) {
        WizzeeLog.debug(`[SAGA ] CANCELLED #${effectId}`);
      },
      actionDispatched() {}, // covered by actionLogger
    }
  : undefined;

// Create the saga middleware
const sagaMiddleware = createSagaMiddleware({ sagaMonitor });

// include saga + optional actionLogger in middleware array
const extraMiddleware = [sagaMiddleware];
if (REDUX_DEBUG) {
  extraMiddleware.push(actionLogger);
}


// Configure the Redux store with the middleware
export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      thunk: false,
      immutableCheck: false,
      serializableCheck: false
    }).concat(...extraMiddleware),
});

// Run the root saga
sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);